/**
 * Ce que chaque écran prend d'une facture fournisseur lue par le Pack IA.
 *
 * Le serveur rend une seule forme (fournisseur, numéro, montants, IBAN,
 * lignes) ; ces fonctions la ramènent aux champs de chaque formulaire. Un
 * champ que la lecture n'a pas trouvé reste absent : le formulaire garde alors
 * ce que l'utilisateur y avait mis.
 */

/** Arrondi au centime. */
const auCentime = (n) => Math.round(n * 100) / 100;

/** Les mots significatifs d'une désignation, sans accents ni casse. */
const mots = (texte) =>
  String(texte || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9]+/)
    .filter((m) => m.length > 2);

/**
 * Les champs d'en-tête : fournisseur, numéro, montant à payer, IBAN, métier.
 *
 * @param {object} lu réponse du serveur
 * @returns {{fournisseur?: string, ref?: string, montant?: number, iban?: string,
 *   metier?: string, date?: string}} les seuls champs lus
 */
export function champsFacture(lu) {
  const champs = {};
  // L'adresse suit le nom, dans le même champ : la facture fournisseur n'a pas
  // de case d'adresse, et le patron la voulait retrouver avec le fournisseur.
  if (lu?.fournisseur) {
    champs.fournisseur = (lu.adresse ? lu.fournisseur + ', ' + lu.adresse : lu.fournisseur).slice(
      0,
      200,
    );
  }
  if (lu?.numero) champs.ref = lu.numero;
  if (lu?.montantTtc != null) champs.montant = +lu.montantTtc;
  if (lu?.iban) champs.iban = lu.iban;
  if (lu?.metier) champs.metier = lu.metier;
  if (lu?.date) champs.date = lu.date;
  // La TVA imprimée sur la facture : elle se récupère dans la position TVA.
  if (lu?.montantTva != null) champs.montantTva = +lu.montantTva;
  if (Array.isArray(lu?.tauxTva) && lu.tauxTva.length === 1) champs.tauxTva = +lu.tauxTva[0];
  return champs;
}

/**
 * La ligne de la facture qui porte un article du stock : celle dont la
 * désignation partage le plus de mots avec son nom (ou sa référence), ou la
 * seule ligne d'une facture d'une ligne.
 *
 * @param {object} lu réponse du serveur
 * @param {{nom?: string, ref?: string}} article l'article réapprovisionné
 * @returns {object|null} la ligne, ou null si aucune ne lui correspond
 */
export function ligneDeLArticle(lu, article) {
  const lignes = lu?.lignes || [];
  if (lignes.length === 1) return lignes[0];
  const cherches = mots(article?.nom);
  let meilleure = null;
  let score = 0;
  for (const l of lignes) {
    if (article?.ref && l.reference && l.reference === article.ref) return l;
    const siens = new Set(mots(l.designation));
    const communs = cherches.filter((m) => siens.has(m)).length;
    const s = cherches.length ? communs / cherches.length : 0;
    if (s > score) {
      score = s;
      meilleure = l;
    }
  }
  return score >= 0.5 ? meilleure : null;
}

/**
 * Ce que l'achat d'un article reprend de la facture : fournisseur, quantité et
 * total TVA comprise de sa ligne.
 *
 * @param {object} lu réponse du serveur
 * @param {object} article l'article réapprovisionné
 * @param {number} tauxDefaut taux de TVA appliqué si la facture n'en dit rien
 * @returns {{fournisseur?: string, qte?: number, total?: number, trouve: boolean}} les champs lus
 */
export function achatDepuisFacture(lu, article, tauxDefaut) {
  const champs = { trouve: false };
  if (lu?.fournisseur) champs.fournisseur = lu.fournisseur;
  const ligne = ligneDeLArticle(lu, article);
  if (!ligne) return champs;
  champs.trouve = true;
  if (ligne.quantite != null) champs.qte = +ligne.quantite;
  const taux = (lu?.tauxTva || [])[0] ?? tauxDefaut ?? 0;
  if (ligne.montant != null) champs.total = auCentime(+ligne.montant * (1 + taux / 100));
  return champs;
}

/**
 * Les lignes du tableau d'ajout en lot tirées d'une facture lue : nom,
 * quantité reçue et prix d'achat. Le prix de vente reste à compléter, c'est
 * la seule colonne que la facture d'un fournisseur ne peut pas donner.
 *
 * @param {object} lu réponse du serveur
 * @returns {{nom: string, qte: string, pa: string, pv: string}[]} une ligne par article de la facture
 */
export function lignesLotDepuisFacture(lu) {
  return (lu?.lignes || [])
    .filter((l) => (l.designation || '').trim())
    .map((l) => ({
      nom: l.designation.trim(),
      qte: l.quantite != null ? String(l.quantite) : '',
      pa: l.prixUnitaire != null ? String(l.prixUnitaire) : '',
      pv: '',
    }));
}
